import { useCallback, useEffect, useState } from 'react';

// Doll — scripted in-studio tutorial host. Front-end only: no AI, no Router,
// no Telegram. Her voice is hand-written here; she just guides a new creator
// through their first game. Auto-shows once for first-time visitors.

const DOLL_AVATAR = '/doll.png';
const STORAGE_KEY = 'yip_tutorial_done';

interface Step {
  target?: string; // matches a [data-tutorial="..."] element to highlight
  short: string;
  more?: string;
  cta?: string;
}

const STEPS: Step[] = [
  {
    short:
      "I'm Doll. I help Kaya build things. You already have a Player — that box in the middle. Let's make it a game.",
    more:
      "A minute, no more. A few small steps and the box becomes something you can play. I'll point; you click.",
    cta: 'Show me',
  },
  {
    target: 'templates',
    short:
      'In a hurry? **Templates** hands you a finished game to tweak. Or build it by hand with me.',
    more:
      "A template drops in a whole working scene. We'll go by hand this time — it's the fastest way to learn where the pieces live.",
  },
  {
    target: 'add-library',
    short:
      'A game needs a point. From the **library**, add a few **Coins** for your Player to collect.',
    more:
      "Everything lives here — characters, props, coins, blocks. Filter with the pills along the top, or search. Coins already know they're collectible; you don't tell them, they just are.",
  },
  {
    target: 'add-library',
    short: 'Every game needs an ending. From the same library, add a **Goal** to reach.',
    more:
      'Collect the coins, touch the goal, you win. Quietly, you just designed a whole game loop.',
  },
  {
    target: 'behaviors',
    short:
      'Want to change how it plays? Select something and open its **settings**, just under the tags.',
    more:
      'Player speed, what a coin is worth, lives, even a time limit — small dials, big difference. Most of building is knowing which little thing to turn.',
  },
  {
    target: 'play',
    short: "That's enough from me. Press **Play** and see what you made.",
    more:
      'Arrow keys or the stick to move. Grab the coins, reach the goal. If it feels good, someone else will think so too.',
    cta: 'Got it',
  },
  {
    short: 'You made a game. You didn\u2019t need me much — but I was here.',
    more:
      "Change the Player's look, add a hazard, set a timer, share the link. I'm usually working somewhere in the background. I'll be here when you come back.",
    cta: 'Done',
  },
];

// Render **bold** spans without a markdown dependency.
function renderText(text: string) {
  return text.split(/(\*\*[^*]+\*\*)/g).map((part, idx) =>
    part.startsWith('**') && part.endsWith('**') ? (
      <strong key={idx} className="text-white font-semibold">
        {part.slice(2, -2)}
      </strong>
    ) : (
      <span key={idx}>{part}</span>
    )
  );
}

export default function TutorialOverlay() {
  const [open, setOpen] = useState(false);
  const [i, setI] = useState(0);
  const [expanded, setExpanded] = useState(false);
  const [rect, setRect] = useState<DOMRect | null>(null);

  // Auto-open once for first-time visitors.
  useEffect(() => {
    try {
      if (!localStorage.getItem(STORAGE_KEY)) setOpen(true);
    } catch {
      /* storage unavailable — just don't auto-open */
    }
  }, []);

  const step = STEPS[i];

  // Highlight the step's target element (if present and on screen).
  useEffect(() => {
    setExpanded(false);
    if (!open || !step.target) {
      setRect(null);
      return;
    }
    const el = document.querySelector(
      `[data-tutorial="${step.target}"]`
    ) as HTMLElement | null;
    if (!el) {
      setRect(null);
      return;
    }
    el.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    const update = () => setRect(el.getBoundingClientRect());
    const t = window.setTimeout(update, 80);
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);
    return () => {
      window.clearTimeout(t);
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
    };
  }, [open, i, step]);

  const finish = useCallback(() => {
    try {
      localStorage.setItem(STORAGE_KEY, '1');
    } catch {
      /* ignore */
    }
    setOpen(false);
    setI(0);
    setRect(null);
  }, []);

  const next = () => {
    if (i < STEPS.length - 1) setI(i + 1);
    else finish();
  };
  const back = () => {
    if (i > 0) setI(i - 1);
  };

  // Collapsed launcher — lets anyone replay the tour.
  if (!open) {
    return (
      <button
        onClick={() => {
          setI(0);
          setOpen(true);
        }}
        title="Tutorial with Doll"
        className="fixed bottom-4 right-4 z-40 flex items-center gap-2 bg-gray-900/90 border border-white/15 rounded-full pl-1.5 pr-4 py-1.5 shadow-xl hover:border-white/30 transition-colors"
      >
        <img
          src={DOLL_AVATAR}
          alt="Doll"
          className="w-9 h-9 rounded-full object-cover border border-white/20"
        />
        <span className="text-sm text-gray-200">Ask Doll</span>
      </button>
    );
  }

  const isLast = i === STEPS.length - 1;

  return (
    <>
      {rect && (
        <div
          className="fixed z-40 pointer-events-none rounded-lg"
          style={{
            left: rect.left - 6,
            top: rect.top - 6,
            width: rect.width + 12,
            height: rect.height + 12,
            border: '2px solid #2B8CFF',
            boxShadow:
              '0 0 0 4px rgba(43,140,255,0.25), 0 0 22px rgba(43,140,255,0.55)',
          }}
        />
      )}

      <div className="fixed bottom-4 right-4 z-50 w-80 max-w-[calc(100vw-2rem)] bg-gray-900 border border-white/15 rounded-2xl shadow-2xl p-4">
        <div className="flex items-center gap-3 mb-3">
          <img
            src={DOLL_AVATAR}
            alt="Doll"
            className="w-12 h-12 rounded-full object-cover border border-white/20 flex-none"
          />
          <div className="min-w-0">
            <div className="text-white font-semibold text-sm">Doll</div>
            <div className="text-gray-500 text-xs">
              Step {i + 1} of {STEPS.length}
            </div>
          </div>
          <button
            onClick={finish}
            className="ml-auto text-gray-500 hover:text-white text-xs"
          >
            Skip
          </button>
        </div>

        <p className="text-gray-200 text-sm leading-relaxed">
          {renderText(step.short)}
        </p>
        {expanded && step.more && (
          <p className="text-gray-400 text-sm leading-relaxed mt-2">
            {renderText(step.more)}
          </p>
        )}

        <div className="flex items-center gap-2 mt-4">
          {step.more && !expanded && (
            <button
              onClick={() => setExpanded(true)}
              className="text-blue-400 text-xs hover:text-blue-300"
            >
              Tell me more
            </button>
          )}
          <div className="ml-auto flex items-center gap-2">
            {i > 0 && (
              <button
                onClick={back}
                className="px-3 py-1.5 rounded text-xs text-gray-300 hover:bg-gray-700"
              >
                Back
              </button>
            )}
            <button
              onClick={next}
              className="px-4 py-1.5 rounded text-xs font-semibold text-white"
              style={{ backgroundImage: 'linear-gradient(90deg,#2B8CFF,#7C3AED)' }}
            >
              {step.cta ?? (isLast ? 'Done' : 'Next')}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
